<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute } from 'vue-router';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import { get } from 'lodash';
import CoverView from '@/views/shared/CoverView.vue';
import DescriptionBlock from '@/components/shared/blocks/DescriptionBlock.vue';
import InstitutionBlock from '@/components/shared/blocks/InstitutionBlock.vue';
import {
  IBookingResource,
  useBookingResourcesStore,
} from '@/stores/bookingResources';
import constants from '@/utils/constants';
import TitleBlock from '@/components/shared/blocks/TitleBlock.vue';
import { useInstitutionStore } from '@/stores/institution';
import StandardBookingFlow from '@/views/booking/StandardBookingFlow.vue';
import TabSwitch, { ITab } from '@/components/shared/TabSwitch.vue';
import i18n from '@/utils/i18n';
import DepartmentsBlock from '@/components/shared/blocks/DepartmentsBlock.vue';
import StandardResourceEntry from '@/views/booking/StandardResourceEntry.vue';
import SlottedResourceEntry from '@/views/booking/SlottedResourceEntry.vue';
import BlocksWrapper from '@/components/shared/blocks/BlocksWrapper.vue';
import { useMatomo } from '@/stores/useMatomo';
import { eventBus } from '@/utils/eventBus';
import { useI18n } from 'vue-i18n';
import { BookingResourceFragment } from '@/graphql/custom-types';

const { t } = useI18n();
const bookingResourcesStore = useBookingResourcesStore();
const institutionStore = useInstitutionStore();
const route = useRoute();
const { trackEvent } = useMatomo();

const showStandardBookingFlow = ref<boolean>(false);

const resourceId = ref(route.params.id);

const currentResource = computed((): BookingResourceFragment | undefined => {
  return bookingResourcesStore.all.find((res) => res.id === resourceId.value);
});

const TABS: ITab[] = [
  {
    title: currentResource?.value?.slotted
      ? i18n.global.t('booking.times')
      : i18n.global.t('booking.allBookings'),
    value: 'BOOKINGS',
    icon: ['far', 'fa-list'],
  },
  {
    title: i18n.global.t('global.info'),
    value: 'INFORMATION',
    icon: ['far', 'fa-info-circle'],
  },
];

const selectedTab = ref<ITab>(TABS[0]);

const moduleColor = computed((): string => {
  return route?.meta?.color as string;
});

const imageUrl = computed(() => {
  return get(
    currentResource.value,
    'coverImage.source',
    constants.placeholderImage
  );
});

onBeforeMount(() => {
  trackEvent('View', 'BookingEntry');
});

const newBooking = () => {
  bookingResourcesStore.setNewBooking({
    resourceId: currentResource?.value?.id,
  });

  if (!currentResource?.value?.slotted) {
    showStandardBookingFlow.value = true;
  }
};

const handleBookingSuccess = () => {
  showStandardBookingFlow.value = false;
  eventBus.emit('success', t('booking.completed'));
};

const handleBookingError = () => {
  showStandardBookingFlow.value = false;
  eventBus.emit('anErrorOccurred');
};
</script>

<template>
  <div class="booking">
    <CoverView
      v-if="currentResource?.id"
      :image-url="imageUrl"
      :module-color="moduleColor"
    >
      <div class="bookingContent">
        <TitleBlock :text="currentResource.title" />

        <TabSwitch
          @tab-selected="selectedTab = $event"
          :items="TABS"
          :selectedItem="selectedTab"
        />

        <div v-if="selectedTab.value === 'INFORMATION'">
          <BlocksWrapper>
            <InstitutionBlock
              :title="currentResource.institution.name"
              :image="currentResource.institution.logo?.source"
              v-if="
                institutionStore.isExternalInstitution(
                  currentResource.institution?.id
                )
              "
            />

            <DescriptionBlock
              :description="currentResource.description || ''"
            />

            <DepartmentsBlock
              :departments="currentResource.departments || []"
            />
          </BlocksWrapper>
        </div>

        <SlottedResourceEntry
          v-else-if="
            currentResource.slotted && selectedTab.value === 'BOOKINGS'
          "
          :resource="currentResource"
          :module-color="moduleColor"
        />

        <StandardResourceEntry v-else :resource="currentResource" />
      </div>

      <div class="newBooking flex sticky w-fit" v-if="!currentResource.slotted">
        <ButtonEl
          text="booking.newBooking"
          :icon="['far', 'fa-circle-plus']"
          background="bg-green-400"
          textColor="text-neutral-0"
          class="mr-5 shadow-xsm"
          rounded="full"
          @click="newBooking"
        />
      </div>
    </CoverView>

    <StandardBookingFlow
      v-if="showStandardBookingFlow"
      :resource="currentResource"
      @close="showStandardBookingFlow = false"
      @success="handleBookingSuccess"
      @error="handleBookingError"
    />
  </div>
</template>

<style lang="scss" scoped>
.booking {
  .bookingContent {
    flex: 1;
  }

  .newBooking {
    bottom: 40px;
    margin-right: 20px;
    align-self: flex-end;
  }
}
</style>
